import React, { useState } from 'react';
import dayjs from 'dayjs';

import { Loader } from '../../../../../components/Loader/Loader';
import '../../Widget.scss';
import { browserHistory } from '../../../../../browserHistory';
import { SmsInboundModal } from './SmsInboundModal';
import analyticsWrapper from '../../../../../utils/analyticsWrapper';
import { useGetOrderDeliverySummary } from '../../../../../hooks/useGetOrderDeliverySummary';
import { useGetDailyPickupsSummary } from '../../../../../hooks/useGetDailyPickupsSummary';
import { ButtonComponent } from '../../../../../ReusableComponents/Button/ButtonComponent';
import { RegionCode } from '../../../../../types/orderTypes';
import { MdExpandMore } from 'react-icons/md';
import { AccordionComponent } from '../../../../../ReusableComponents/Accordion/AccordionComponent';

export default function OrdersTodayWidget(): JSX.Element {
  const [showPickups, setShowPickups] = useState(false);
  const [showInboundModal, setShowInboundModal] = useState(false);

  const { deliveryOrdersSummary, isLoading } = useGetOrderDeliverySummary();
  const { pickupsSummary, pickupsLoading } = useGetDailyPickupsSummary();

  const [expanded, setExpanded] = React.useState<string | false>('panel1');

  const handleChange =
    (panel: string | false) =>
    (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  if (isLoading || pickupsLoading) {
    return (
      <div className="widget">
        <div className="widget__header">
          <h5>Remaining orders today</h5>
        </div>
        <div className="widget__loader">
          <Loader type="spinner" size="large" />
        </div>
      </div>
    );
  }
  deliveryOrdersSummary.accounts.sort((a, b) =>
    a.accountName.localeCompare(b.accountName)
  );
  const goToOrderSearch = (query: string) => {
    browserHistory.push(`/orders?q=${query.trim()}`);
  };

  const createAccordionOrdersWidgetList = () => {
    const accordionArray = [];
    const numberOfXlOrders = deliveryOrdersSummary.ordersSizeCount.xxl;
    const numberOfDeviatedBundledOrders =
      deliveryOrdersSummary.ordersBundlesCount.openBundledDeviationCount;

    const numberOfUnbundledDeviatedOrders =
      deliveryOrdersSummary.ordersBundlesCount.openDeviationCount -
      numberOfDeviatedBundledOrders;
    const numberOfRegularOrders =
      deliveryOrdersSummary.ordersSizeCount.dailyTotal -
      numberOfUnbundledDeviatedOrders;
    const numberOfTemperatureOrders =
      deliveryOrdersSummary.ordersSizeCount.temperatureCount;
    const numberOfUnbundledOrders =
      deliveryOrdersSummary.ordersSizeCount.dailyTotal -
      deliveryOrdersSummary.ordersBundlesCount.bundleCount +
      deliveryOrdersSummary.ordersBundlesCount.openBundledDeviationCount;

    const numberOfBundledOrders =
      deliveryOrdersSummary.ordersBundlesCount.bundleCount -
      numberOfDeviatedBundledOrders;
    if (deliveryOrdersSummary) {
      const accordionOrdersObject = {
        expanded: expanded === 'panel1',
        onChange: handleChange('panel1'),
        expandIcon: <MdExpandMore />,
        accordionSummary: {
          element: (
            <div className="widget__accordion__header">
              <span>Orders</span>
              <span>{numberOfRegularOrders}</span>
            </div>
          ),
          className: '',
        },
        accordionDetails: {
          className: 'widget__accordion__details',
          element: (
            <div>
              <div className="widget__accordion__details__text">
                <span>Regular:</span>
                <span>{numberOfRegularOrders}</span>
              </div>
              <div className="widget__accordion__details__text">
                <span>XL:</span>
                <span>{numberOfXlOrders}</span>
              </div>
              <div className="widget__accordion__details__text">
                <span>Temperature:</span>
                <span>{numberOfTemperatureOrders}</span>
              </div>
              <div className="widget__accordion__details__text">
                <span>Unbundled deviations:</span>
                <span>{numberOfUnbundledDeviatedOrders}</span>
              </div>
            </div>
          ),
        },
      };
      accordionArray.push(accordionOrdersObject);
      const accordionBundlesObject = {
        expanded: expanded === 'panel2',
        onChange: handleChange('panel2'),
        expandIcon: <MdExpandMore />,
        accordionSummary: {
          element: (
            <div className="widget__accordion__header">
              <span>Bundled orders</span>
              <span>{numberOfBundledOrders}</span>
            </div>
          ),
          className: '',
        },
        accordionDetails: {
          className: 'widget__accordion__details',
          element: (
            <div>
              <div className="widget__accordion__details__text">
                <span>Bundled:</span>
                <span>{numberOfBundledOrders}</span>
              </div>
              <div className="widget__accordion__details__text">
                <span>Bundled deviations:</span>
                <span>{numberOfDeviatedBundledOrders}</span>
              </div>
            </div>
          ),
        },
      };
      accordionArray.push(accordionBundlesObject);
      const accordionNotBundledNumber = {
        expanded: expanded === 'panel3',
        onChange: handleChange('panel3'),
        expandIcon: <MdExpandMore />,
        accordionSummary: {
          element: (
            <div className="widget__accordion__header">
              <div>Not bundled orders</div>
              <div>{numberOfUnbundledOrders}</div>
            </div>
          ),
          className: '',
        },
        accordionDetails: {
          className: '',
        },
      };
      accordionArray.push(accordionNotBundledNumber);
    }
    return accordionArray;
  };

  return (
    <div className="widget">
      <div className="widget__header__order_stats">
        <h5>TODAY</h5>
      </div>
      <div style={{ height: 10 }} />
      <div className="widget__accordion">
        <AccordionComponent
          accordionDataList={createAccordionOrdersWidgetList()}
        />
      </div>
      <div
        className="widget__button-area"
        style={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <ButtonComponent
          className="widget_button"
          variant="outlined"
          onClick={() => {
            setShowInboundModal(!showInboundModal);
            analyticsWrapper.event({
              category: 'inBoundShop-modal',
              action: `open-modal-btn`,
              label: 'click',
            });
          }}
          disabled={pickupsSummary === null}
        >
          {`${showInboundModal ? 'Hide Pickups' : 'Inbound'}`}
        </ButtonComponent>
        <ButtonComponent
          className="widget_button_show"
          variant="outlined"
          onClick={() => setShowPickups(!showPickups)}
          disabled={pickupsSummary === null}
        >
          {`${showPickups ? 'Hide Pickups' : 'Show Pickups'}`}
        </ButtonComponent>
      </div>
      {showInboundModal && (
        <SmsInboundModal
          date={dayjs(new Date())}
          closeModal={() => setShowInboundModal(false)}
          inboundShopList={pickupsSummary.pickups}
        />
      )}
      {showPickups && (
        <div className="widget__show-more">
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {pickupsSummary.pickups.length
              ? 'Pickups today'
              : 'No Pickups today'}
          </div>

          <div style={{ width: '100%' }}>
            <table className="window-table-clickable" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Company</th>
                  <th align="right">Region</th>
                  <th data-sortmethod="number" align="right">
                    # orders
                  </th>
                </tr>
              </thead>
              <tbody>
                {pickupsSummary.pickups.map(account => (
                  <tr
                    key={account.accountId}
                    onClick={() => goToOrderSearch(account.accountName)}
                  >
                    <td>{account.accountName}</td>
                    <td align="right">{RegionCode[account.region]}</td>
                    <td align="right">{account.orderCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
